/**
 * The version diff (plan docs/plans/lms-plan.md 6.6 "the editor shows the
 * diff of reading_md and slides between two versions", 8.1, 18.17; WP-44;
 * R10, G3), at /lms/manage/catalog/versions/:versionId/diff/:otherId: what
 * changed in :versionId compared with :otherId, section by section, with the
 * reading's paragraph diff and the slides that changed (and which of their
 * fields). There is no automatic merge (G3): a person reads this and edits
 * the draft by hand. Unchanged sections are folded away unless asked for.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { apiErrorMessage } from '../../../api/client';
import { diffVersions } from '../../../api/catalog';
import type { DiffSection, DiffStatus, VersionDiff } from '../../../api/catalog';
import { noticeCls, pillCls } from '../../../components/player/styles';
import { Checkbox } from './fields';
import { catalogPaths } from './model';
import { cardCls, linkCls, microLabelCls, subheadingCls } from './styles';

const STATUS_WORDS: Record<DiffStatus, string> = { added: 'Added', removed: 'Removed', changed: 'Changed', same: 'Unchanged' };
const STATUS_TONE: Record<DiffStatus, 'green' | 'red' | 'amber' | 'gray'> = { added: 'green', removed: 'red', changed: 'amber', same: 'gray' };

export function DiffSectionCard({ section }: { section: DiffSection }) {
  const changedSlides = (section.slides || []).filter((s) => s.status !== 'same');
  const reading = Array.isArray(section.reading_diff) ? section.reading_diff : [];
  return (
    <section className={`${cardCls} space-y-3`} data-testid="lms-diff-section" data-section-id={section.id} data-status={section.status}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className={subheadingCls}>{section.title || `Section ${section.id}`}</h3>
        <span className={pillCls(STATUS_TONE[section.status])}>{STATUS_WORDS[section.status]}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{`id ${section.id}`}</span>
      </div>

      {changedSlides.length > 0 ? (
        <div className="space-y-1">
          <div className={microLabelCls}>Slides</div>
          <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200" data-testid="lms-diff-slides">
            {changedSlides.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{s.id}</span>
                <span className={pillCls(STATUS_TONE[s.status])}>{STATUS_WORDS[s.status]}</span>
                {s.changed_fields && s.changed_fields.length > 0 ? (
                  <span className="text-xs text-gray-600 dark:text-gray-300">{`Fields: ${s.changed_fields.join(', ')}`}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {section.reading_changed && reading.length > 0 ? (
        <div className="space-y-1">
          <div className={microLabelCls}>Reading</div>
          <ol className="space-y-1" data-testid="lms-diff-reading">
            {reading.map((line, i) => (
              <li
                key={i}
                data-op={line.op}
                className={`whitespace-pre-line rounded-lg border px-3 py-2 text-sm ${
                  line.op === 'added'
                    ? 'border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200'
                    : line.op === 'removed'
                      ? 'border-red-200 bg-red-50 text-red-900 line-through decoration-red-400 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200'
                      : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300'
                }`}
              >
                <span className="mr-2 text-[10px] font-semibold uppercase tracking-widest no-underline">
                  {line.op === 'added' ? 'Added' : line.op === 'removed' ? 'Removed' : 'Same'}
                </span>
                {line.text}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {section.status === 'changed' && changedSlides.length === 0 && !section.reading_changed ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">The section title, minutes, why-it-matters line or what-to-remember list changed.</p>
      ) : null}
    </section>
  );
}

export function DiffView() {
  const { versionId, otherId } = useParams();
  const [diff, setDiff] = useState<VersionDiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSame, setShowSame] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDiff(null);
    setError(null);
    if (!versionId || !otherId) {
      setError('Two versions are needed to compare.');
      return undefined;
    }
    diffVersions(versionId, otherId)
      .then((d) => {
        if (!cancelled) setDiff(d);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'The versions could not be compared.'));
      });
    return () => {
      cancelled = true;
    };
  }, [versionId, otherId]);

  const sections = diff && Array.isArray(diff.sections) ? diff.sections : [];
  const changed = sections.filter((s) => s.status !== 'same');
  const shown = showSame ? sections : changed;
  const count = (status: DiffStatus) => sections.filter((s) => s.status === status).length;

  return (
    <div className="space-y-4" data-testid="lms-catalog-diff">
      {versionId ? (
        <Link to={catalogPaths.version(versionId)} className={`inline-flex items-center gap-1 text-sm ${linkCls}`}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to the version
        </Link>
      ) : null}
      {error ? (
        <div className={noticeCls('red')} role="alert">
          {error}
        </div>
      ) : null}
      {!error && !diff ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Comparing the versions...
        </div>
      ) : null}
      {diff ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-testid="lms-diff-title">
              {`What changed in version ${diff.version.version_no} since version ${diff.other.version_no}`}
            </h2>
            <Checkbox label="Show unchanged sections" checked={showSame} onChange={setShowSame} testId="lms-diff-show-same" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="lms-diff-summary">
            {`${count('changed')} changed, ${count('added')} added, ${count('removed')} removed, ${count('same')} unchanged. There is no automatic merge: edit the draft by hand (6.6).`}
          </p>
          {shown.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No section changed between these versions.</p>
          ) : (
            <div className="space-y-3">
              {shown.map((s) => (
                <DiffSectionCard key={`${s.id}-${s.status}`} section={s} />
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default DiffView;
